import React, {
  useEffect,
  useState,
  useRef,
  useContext,
  Fragment,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Listing } from 'models/Listing';
import { Button } from 'ncoded-component-library';
import EmptyScreen from 'components/EmptyScreen';
import api from 'api';
import utils, { getArrayLength } from 'utils';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import ResolvePagination from 'components/ResolvePagination';
import { useNavigate } from 'react-router-dom';
import HelmetWrapper from 'components/HelmetWrapper';
import useHelmetInfo from '../../../hooks/useHelmetInfo';
import AdvertisingBusinessCard from 'components/AdvertisingBusinessCard';
import IndustryFilter from '../../../Business/pages/Businesses/components/filters/IndustryFilter';
import AuthContext from 'providers/Auth/Auth.context';
import UserIcon from 'icons/User.icon';
import BuildingIcon from 'icons/Building.icon';
import { FILTER_FIELDS, LIMIT } from './data';
import { BUSINESS_ROUTE, SIGNUP_ROUTE } from 'data/global';
import BusinessesTitle from '../../../components/BusinessesTitle';
import BusinessesFilters from '../../../components/BusinessesFilters';
import useSiteInitializedState from 'hooks/useSiteInitializedState';
import useSponsoredListings from '../../../hooks/useSponsoredListings';
import useBusinessesCommon from '../../../hooks/useBusinessesCommon';
import useResetPage from '../../../hooks/useResetPage';
import useSearchParams from 'hooks/useSearchParams';
import useBusinessesFilters from '../../../hooks/useBusinessesFilters';
import useUrgentCampaigns from './hooks/useUrgentCampaigns';
import CorrectTierCard from 'components/CorrectTierCard';
import useListingCardPlaceholder from 'hooks/useListingCardPlaceholder';
import { DraftListingBanner } from 'components/DraftListingBanner';
import NoResultBanner from 'components/NoResultBanner';

import './UrgentCampaigns.styles.scss';
import './UrgentCampaigns.styles.responsive.scss';
import '../../../BusinessesCommon.styles.scss';
import '../../../BusinessesCommon.styles.responsive.scss';

type UrgentCampaignsProps = {
  className?: string;
};

const UrgentCampaigns: React.FC<UrgentCampaignsProps> = (props) => {
  const { className } = props;

  const [loading, setLoading] = useState(utils.isSSGHuman());
  const [totalPages, setTotalPages] = useState(0);

  const [listings, setListings] =
    useSiteInitializedState<Array<Listing>>('listings-array');

  const { isLoggedIn } = useContext(AuthContext);
  const { popServerError } = useContext(PopNotificationsContext);

  const latestFetchId = useRef(0);

  const classes = classNames(
    'urgent-campaigns',
    'businesses-common',
    className,
  );

  const listingsLength = getArrayLength(listings);

  const { t } = useTranslation();

  const navigate = useNavigate();

  const { breadcrumbsLinks } = useUrgentCampaigns();

  const { numOfActiveFilter } = useBusinessesFilters(true);

  const searchParams = useSearchParams();

  const { $page: currentPage } = searchParams;

  const { title, description, url } = useHelmetInfo(currentPage, true);

  const { sponsoredAdvert } = useSponsoredListings(currentPage);

  const placeholder = useListingCardPlaceholder(+LIMIT);

  useBusinessesCommon();

  useResetPage(totalPages, currentPage);

  useEffect(() => {
    !utils.isSSGHuman() &&
      (async () => {
        try {
          setLoading(true);
          setListings([]);
          const currentFetchId = +new Date();
          latestFetchId.current = currentFetchId;

          const {
            data: { totalPages, items },
          } = await api.listings.getDistressedListings({
            $limit: LIMIT,
            ...searchParams,
          });

          if (latestFetchId.current === currentFetchId) {
            setTotalPages(totalPages);
            setListings(items);
          }
        } catch (e) {
          popServerError(e);
        } finally {
          setLoading(false);
        }
      })();
  }, [searchParams, popServerError, setListings]);

  if (!loading && !listingsLength && !numOfActiveFilter) {
    return (
      <div className={classes}>
        <HelmetWrapper title={title} description={description} url={url} />

        <BusinessesTitle
          title={t('sorryNoUrgnetListingsNow')}
          withPage={false}
          breadcrumbsLinks={breadcrumbsLinks}
        >
          <p>{t('weExpectSomeSoon')}</p>

          {!isLoggedIn && <p>{t('createAccountToBeNotified')}</p>}

          <div className="empty-state-actions">
            {!isLoggedIn && (
              <Button
                variant="outline"
                icon={UserIcon}
                onClick={() => navigate(SIGNUP_ROUTE)}
              >
                {t('createAccount')}
              </Button>
            )}
            <Button
              variant="link"
              icon={BuildingIcon}
              onClick={() => navigate(BUSINESS_ROUTE)}
            >
              {t('browseGoingConcerns')}
            </Button>
          </div>
        </BusinessesTitle>
      </div>
    );
  }

  return (
    <div className={classes}>
      <HelmetWrapper title={title} description={description} url={url} />

      <BusinessesTitle
        title={t('urgentListings')}
        currentPage={currentPage}
        breadcrumbsLinks={breadcrumbsLinks}
      />

      <BusinessesFilters fields={FILTER_FIELDS}>
        <IndustryFilter />
      </BusinessesFilters>

      <div className="businesses-common__container">
        <div className="businesses-common__listings">
          <div className="businesses-common__listings__container">
            {loading ? (
              placeholder
            ) : !listingsLength ? (
              numOfActiveFilter ? (
                <NoResultBanner title={t('noResultsWithFilters')} />
              ) : (
                <EmptyScreen
                  title={t('noBusinessFound')}
                  content={t('noBusinessesForSearhcedCriteria')}
                />
              )
            ) : (
              listings.map((listing, index) => {
                const Card = (
                  <CorrectTierCard
                    key={listing._id}
                    listing={listing}
                    updateListing={(listingId, updatedValues) =>
                      setListings((old) =>
                        old.map((el) =>
                          el._id !== listingId
                            ? el
                            : { ...el, ...updatedValues },
                        ),
                      )
                    }
                  />
                );

                if (
                  sponsoredAdvert &&
                  ((listingsLength < 5 && index === listingsLength - 1) ||
                    (listingsLength >= 5 && index === 4))
                ) {
                  return (
                    <Fragment key={sponsoredAdvert._id}>
                      {Card}
                      <AdvertisingBusinessCard
                        title={sponsoredAdvert.title}
                        description={sponsoredAdvert.description}
                        coverPhoto={sponsoredAdvert.coverPhoto}
                        link={sponsoredAdvert.link}
                        logoImage={sponsoredAdvert.logoImage}
                        ctaText={sponsoredAdvert.ctaText}
                      />
                    </Fragment>
                  );
                }

                return Card;
              })
            )}
          </div>

          <div className="pagination-container">
            <ResolvePagination totalPages={totalPages} />
          </div>
        </div>
      </div>

      <DraftListingBanner />
    </div>
  );
};

export default UrgentCampaigns;
